import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  confirmSelfCheck,
  enterAssessment,
  fetchAssessment,
  fetchNextReview,
  resubmitPortfolio,
  submitReview,
  type AssessmentView,
  type ReviewTask,
} from "../../api/assessment";
import { fetchContent } from "../../api/content";
import {
  completePaidReady,
  confirmInvoice,
  fetchLaunchPlan,
  fetchPaidReady,
  saveInvoice,
  savePayment,
  setKycStatus,
  uploadKycScreenshot,
  type Invoice,
  type PaidReadyView,
} from "../../api/paidReady";
import { Banner } from "../../components/Banner";
import { BackButton } from "../../components/BackButton";
import { Button } from "../../components/Button";
import { ScreenHeader } from "../../components/ScreenHeader";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { registerDraftSender, saveOrQueue } from "../../lib/offlineSync";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { useDebouncedCallback } from "../../lib/useDebounced";
import {
  ResubmitScreen,
  ResultScreen,
  ReviewHubScreen,
  ReviewScreen,
  SelfCheckScreen,
} from "../../screens/paidReady/AssessmentScreen";
import { InvoiceScreen } from "../../screens/paidReady/InvoiceScreen";
import { LaunchPlanScreen } from "../../screens/paidReady/LaunchPlanScreen";
import { PaidReadyScreen } from "../../screens/paidReady/PaidReadyScreen";

registerDraftSender<Invoice>("invoice", saveInvoice);

export function PaidReadyRoute() {
  const navigate = useNavigate();
  const loaded = useAsync(() => Promise.all([fetchPaidReady(), fetchContent()]), []);
  const [view, setView] = useState<PaidReadyView | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setView(loaded.data[0]);
  }, [loaded.data]);

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view && loaded.data ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <PaidReadyScreen
            view={view}
            payMethods={loaded.data[1].payMethods}
            uploadingPlatform={uploading}
            uploadError={uploadError}
            busy={busy}
            onPayment={(methods, confirmed) => {
              setView({
                ...view,
                step: { ...view.step, payMethods: methods, payConfirmed: confirmed },
              });
              void attempt(
                () => undefined,
                setError,
                async () => setView(await savePayment(methods, confirmed)),
              );
            }}
            onKycStatus={(platformId, status) =>
              void attempt(
                () => undefined,
                setError,
                async () => setView(await setKycStatus(platformId, status)),
              )
            }
            onKycFile={(platformId, file) => {
              setUploading(platformId);
              setUploadError(null);
              uploadKycScreenshot(platformId, file)
                .then(setView)
                .catch((e: unknown) => setUploadError(messageOf(e)))
                .finally(() => setUploading(null));
            }}
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completePaidReady());
                // Only the first completion opens the Launch Plan; re-saving a finished step stays here.
                if (!wasDone) void navigate("/launch-plan", { state: { justDone: true } });
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

export function InvoiceRoute() {
  const { user } = useSession();
  const loaded = useAsync(fetchPaidReady, []);
  const [view, setView] = useState<PaidReadyView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setView(loaded.data);
  }, [loaded.data]);

  const saveSoon = useDebouncedCallback((invoice: Invoice) => {
    setMessage(null);
    void saveOrQueue("invoice", "invoice", invoice, async (value) =>
      setView(await saveInvoice(value)),
    ).catch((e: unknown) => setError(messageOf(e)));
  }, 400);

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view && user ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <InvoiceScreen
            view={view}
            fullName={`${user.firstName} ${user.lastName}`.trim()}
            email={user.email}
            busy={busy}
            message={message}
            onChange={saveSoon}
            onSave={() =>
              void attempt(setBusy, setError, async () => {
                setView(await confirmInvoice());
                setMessage("✓ Invoice saved.");
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

type Mode = "overview" | "review" | "result" | "resubmit";

/**
 * The assessment flow. It always enters (or refreshes) the pool first, so a changed Step 2 link starts a new round,
 * then routes to whichever screen the server's state calls for.
 */
export function AssessmentRoute() {
  const loaded = useAsync(() => Promise.all([enterAssessment(), fetchContent()]), []);
  const [view, setView] = useState<AssessmentView | null>(null);
  const [mode, setMode] = useState<Mode>("overview");
  const [task, setTask] = useState<ReviewTask | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setView(loaded.data[0]);
  }, [loaded.data]);

  const refresh = () =>
    void attempt(setBusy, setError, async () => setView(await fetchAssessment()));

  const startReview = () =>
    void attempt(setBusy, setError, async () => {
      const next = await fetchNextReview();
      setTask(next);
      if (next) setMode("review");
      else {
        setView(await fetchAssessment());
        setMode("result");
      }
    });

  const content = loaded.data?.[1];
  let body: React.ReactNode = null;

  if (view && content) {
    const reviewsDone = view.reviewsAssigned > 0 && view.reviewsDone >= view.reviewsAssigned;
    if (!view.structPass || mode === "result") {
      body = (
        <ResultScreen
          view={view}
          onReview={startReview}
          onResubmit={() => setMode("resubmit")}
          onRefresh={refresh}
        />
      );
    } else if (mode === "resubmit") {
      body = (
        <ResubmitScreen
          currentUrl={view.url ?? ""}
          busy={busy}
          error={error}
          onBack={() => setMode("result")}
          onSubmit={(url, confirmed) =>
            void attempt(setBusy, setError, async () => {
              setView(await resubmitPortfolio(url, confirmed));
              setMode("overview");
            })
          }
        />
      );
    } else if (!view.selfCheckConfirmed) {
      body = (
        <SelfCheckScreen
          view={view}
          checklist={content.checklist}
          busy={busy}
          onSave={(keys) =>
            void attempt(setBusy, setError, async () => setView(await confirmSelfCheck(keys)))
          }
        />
      );
    } else if (mode === "review" && task) {
      body = (
        <ReviewScreen
          key={task.assignmentId}
          task={task}
          position={view.reviewsDone + 1}
          total={view.reviewsAssigned}
          checklist={content.checklist}
          busy={busy}
          error={error}
          onPause={() => setMode("overview")}
          onSubmit={(scores, seen) =>
            void attempt(setBusy, setError, async () => {
              setView(await submitReview(task.assignmentId, scores, seen));
              const next = await fetchNextReview();
              setTask(next);
              if (!next) setMode("result");
            })
          }
        />
      );
    } else if (reviewsDone) {
      body = (
        <ResultScreen
          view={view}
          onReview={startReview}
          onResubmit={() => setMode("resubmit")}
          onRefresh={refresh}
        />
      );
    } else {
      body = <ReviewHubScreen view={view} onStart={startReview} onRefresh={refresh} />;
    }
  }

  return (
    <ScreenState
      loading={loaded.loading}
      error={loaded.error}
      onRetry={loaded.reload}
      renderError={(e) => (
        <div>
          <ScreenHeader
            kicker="Step 4 · Readiness assessment"
            title="We need your portfolio first."
            sub={e.message}
          />
          <BackButton to="/gig/2" label="Go to Step 2" />
        </div>
      )}
    >
      {error && mode !== "review" && mode !== "resubmit" ? (
        <Banner tone="bad">{error}</Banner>
      ) : null}
      {body}
    </ScreenState>
  );
}

export function LaunchPlanRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const loaded = useAsync(fetchLaunchPlan, []);
  const justDone = Boolean((location.state as { justDone?: boolean } | null)?.justDone);
  return (
    <ScreenState
      loading={loaded.loading}
      error={loaded.error}
      onRetry={loaded.reload}
      renderError={(e) => (
        <div>
          <ScreenHeader kicker="Launch Plan" title="Not yet." sub={e.message} />
          <Button onClick={() => void navigate("/gig/4")}>Go to Step 4</Button>
        </div>
      )}
    >
      {loaded.data ? <LaunchPlanScreen plan={loaded.data} justDone={justDone} /> : null}
    </ScreenState>
  );
}
